import { FC } from 'react';
import { Box } from '@mui/material';
import { GRNStatus } from 'src/models/enums/GRNStatus.enum';
import Status from 'src/components/Status';
import { GRNViewData } from 'src/models/Management.interface';
import {
  formatDate,
  formatCurrency,
  formatNumber,
} from 'src/utils/format.utils';
import { getGRNStatusText } from 'src/utils/status.utils';
import {
  StyledDialog,
  FixedHeaderWrapper,
  ContentWrapper,
  HeaderBox,
  TitleBox,
  TitleTypography,
  CloseIcon,
  ContentBox,
  MainContentGrid,
  LeftColumn,
  RightColumn,
  VendorCreatedRow,
  FieldBox,
  LabelTypography,
  ValueTypography,
  ApprovalInfoBox,
  ApprovalRow,
  InvoiceSectionBox,
  InvoiceRow,
  InvoiceFieldBox,
  InvoiceFieldBoxRight,
  AmountValueTypography,
  TotalPaidTypography,
  TotalBalanceLabel,
  BalanceLabel,
  BalanceValue,
  NoteSectionBox,
  NoteLabelTypography,
  NoteValueTypography,
  ItemsSectionTitle,
  ItemsHeaderBox,
  ItemsRowBox,
  ItemColumn,
  ItemValueColumn,
  RejectionNoteLabel,
  RejectionNoteText,
  FieldBoxRight,
  ItemsContainer,
  RejectionNoteSectionBox,
  PaymentsLink,
} from './ViewGRNDialog.styles';

interface ViewGRNDialogProps {
  open: boolean;
  onClose: () => void;
  data: GRNViewData | null;
  onCheckPayments?: () => void;
}

const ViewGRNDialog: FC<ViewGRNDialogProps> = ({
  open,
  onClose,
  data,
  onCheckPayments,
}) => {
  if (!data) return null;

  const numStatus = Number(data.status);
  const isApproved = numStatus === GRNStatus.APPROVED;
  const isRejected = numStatus === GRNStatus.REJECTED;
  const isPaid = numStatus === GRNStatus.PAID;
  const isPaymentProgress = numStatus === GRNStatus.PAYMENT_PROGRESS;
  const showApprovalInfo = isApproved || isPaid || isPaymentProgress;
  const showRejectionInfo = isRejected;

  // Calculate paid amounts
  const totalPaidAmount = data.totalPaidAmount ?? 0;
  const paidToBeAmount = (data.invoiceAmount || 0) - totalPaidAmount;
  const hasPaymentInfo = isPaymentProgress || isPaid;
  const showCheckPayment = isApproved || isPaymentProgress;

  return (
    <StyledDialog open={open} onClose={onClose}>
      {/* Fixed Header Section */}
      <FixedHeaderWrapper>
        <HeaderBox>
          <TitleBox>
            <TitleTypography>GRN Details</TitleTypography>
            <Status status={getGRNStatusText(data.status)} />
          </TitleBox>
          <CloseIcon component="i" className="bx bx-x" onClick={onClose} />
        </HeaderBox>
      </FixedHeaderWrapper>

      {/* Scrollable Content */}
      <ContentWrapper>
        <ContentBox>
          {/* Vendor and Created Date / Approval or Rejection Box */}
          <MainContentGrid>
            <LeftColumn>
              <LeftColumn>
                <VendorCreatedRow>
                  <FieldBox>
                    <LabelTypography>Vendor</LabelTypography>
                    <ValueTypography>{data.vendorName || '-'}</ValueTypography>
                  </FieldBox>
                  <FieldBoxRight>
                    <LabelTypography>Created Date</LabelTypography>
                    <ValueTypography>
                      {formatDate(data.createdAt)}
                    </ValueTypography>
                  </FieldBoxRight>
                </VendorCreatedRow>
                <FieldBox>
                  <LabelTypography>Prepared by</LabelTypography>
                  <ValueTypography>{data.preparedBy || '-'}</ValueTypography>
                </FieldBox>
              </LeftColumn>
              {/* Invoice Section */}
              <InvoiceSectionBox>
                <InvoiceRow>
                  <InvoiceFieldBox>
                    <LabelTypography>Invoice Number</LabelTypography>
                    <ValueTypography>
                      {data.invoiceNumber || '-'}
                    </ValueTypography>
                  </InvoiceFieldBox>
                  <InvoiceFieldBoxRight>
                    <LabelTypography>Invoice Amount</LabelTypography>
                    <AmountValueTypography>
                      {formatCurrency(data.invoiceAmount)}
                    </AmountValueTypography>
                  </InvoiceFieldBoxRight>
                </InvoiceRow>

                {hasPaymentInfo && (
                  <InvoiceRow>
                    <InvoiceFieldBox>
                      <TotalBalanceLabel>Total Paid Amount</TotalBalanceLabel>
                      <TotalPaidTypography>
                        {formatCurrency(totalPaidAmount)}
                      </TotalPaidTypography>
                    </InvoiceFieldBox>
                    <InvoiceFieldBoxRight>
                      {isPaid ? (
                        <PaymentsLink onClick={() => onCheckPayments?.()}>
                          Payments
                        </PaymentsLink>
                      ) : (
                        <>
                          <BalanceLabel>Paid to be Amount</BalanceLabel>
                          <BalanceValue>
                            {formatCurrency(paidToBeAmount)}
                          </BalanceValue>
                        </>
                      )}
                    </InvoiceFieldBoxRight>
                  </InvoiceRow>
                )}

                {showCheckPayment && (
                  <PaymentsLink onClick={() => onCheckPayments?.()}>
                    Payments
                  </PaymentsLink>
                )}
              </InvoiceSectionBox>
              <NoteSectionBox>
                <NoteLabelTypography>Note</NoteLabelTypography>
                <NoteValueTypography>
                  {data.notes || 'No notes available.'}
                </NoteValueTypography>
              </NoteSectionBox>
            </LeftColumn>
            <RightColumn>
              {showApprovalInfo && (
                <ApprovalInfoBox>
                  <ApprovalRow>
                    <FieldBox>
                      <LabelTypography>Approved Date</LabelTypography>
                      <ValueTypography>
                        {formatDate(data.approvedDate)}
                      </ValueTypography>
                    </FieldBox>
                    <FieldBoxRight>
                      <LabelTypography>Approved User</LabelTypography>
                      <ValueTypography>
                        {data.approvedBy || '-'}
                      </ValueTypography>
                    </FieldBoxRight>
                  </ApprovalRow>
                </ApprovalInfoBox>
              )}
              {showRejectionInfo && (
                <ApprovalInfoBox>
                  <ApprovalRow>
                    <FieldBox>
                      <LabelTypography>Rejected Date</LabelTypography>
                      <ValueTypography>
                        {formatDate(data.rejectedDate || data.approvedDate)}
                      </ValueTypography>
                    </FieldBox>
                    <FieldBoxRight>
                      <LabelTypography>Rejected User</LabelTypography>
                      <ValueTypography>
                        {data.rejectedBy || data.approvedBy || '-'}
                      </ValueTypography>
                    </FieldBoxRight>
                  </ApprovalRow>
                  {data.rejectedNotes && (
                    <RejectionNoteSectionBox>
                      <RejectionNoteLabel>Rejection Note</RejectionNoteLabel>
                      <RejectionNoteText>
                        {data.rejectedNotes}
                      </RejectionNoteText>
                    </RejectionNoteSectionBox>
                  )}
                </ApprovalInfoBox>
              )}
            </RightColumn>
          </MainContentGrid>

          {/* Items Section */}
          <Box>
            <ItemsSectionTitle>Items</ItemsSectionTitle>
            <ItemsContainer>
              <ItemsHeaderBox>
                <ItemColumn width="40%">Item</ItemColumn>
                <ItemColumn width="20%" align="right">
                  Received Quantity
                </ItemColumn>
                <ItemColumn width="20%" align="right">
                  Returned Quantity
                </ItemColumn>
                <ItemColumn width="20%" align="right">
                  Unit price
                </ItemColumn>
              </ItemsHeaderBox>
              {data.items && data.items.length > 0 ? (
                data.items.map((item) => (
                  <ItemsRowBox key={item.id}>
                    <ItemValueColumn width="40%">
                      {item.rawMaterialName}
                    </ItemValueColumn>
                    <ItemValueColumn width="20%" align="right">
                      {formatNumber(item.receivedQty)}
                    </ItemValueColumn>
                    <ItemValueColumn width="20%" align="right">
                      {formatNumber(item.returnedQty)}
                    </ItemValueColumn>
                    <ItemValueColumn width="20%" align="right">
                      {formatCurrency(item.unitPrice)}
                    </ItemValueColumn>
                  </ItemsRowBox>
                ))
              ) : (
                <ItemsRowBox>
                  <ItemValueColumn>No items found.</ItemValueColumn>
                </ItemsRowBox>
              )}
            </ItemsContainer>
          </Box>
        </ContentBox>
      </ContentWrapper>
    </StyledDialog>
  );
};

export default ViewGRNDialog;
